import { useCallback } from 'react';
import {
  keepPreviousData,
  QueryKey,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  fetchRSimEnability,
  RSimEnabilities,
  setRSimEnability,
} from 'rSimInventory/RSimApi/rSimApi';
import { AlertTitle } from 'common/Snackbar/AlertTitle';
import { AutoHideSnackbar } from 'common/Snackbar/AutoHideSnackbar';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { Actions, Subjects } from 'permissions/ability';
import { useAbility } from 'permissions/hooks/useAbility';
import { rSimProductsQueries } from '../utils/rsimProductsQueries';
import { KickSimContext, KickSimMutationArgs } from './KickRSimContext';

type KickRsimProviderProps = {
  children: React.ReactNode;
  euiccs: string[];
  queryKey?: QueryKey;
};

export const KickRsimProvider: React.FC<KickRsimProviderProps> = ({
  children,
  euiccs,
  queryKey,
}) => {
  const { t } = useTranslation();
  const ability = useAbility();
  const { data: rsimEnability } = useQuery({
    queryKey: queryKey ? [queryKey] : rSimProductsQueries.enability({ euiccs }),
    queryFn: ({ signal }) => fetchRSimEnability(euiccs, signal),
    placeholderData: keepPreviousData,
    gcTime: 0,
    enabled: ability.can(Actions.simulate, Subjects.outage),
    // suspense: false,
  });

  const isPrimaryProfileDisabled = useCallback(
    (euicc: keyof RSimEnabilities) => {
      if (!rsimEnability) {
        return false;
      }

      return rsimEnability?.[euicc]?.isPrimaryProfileDisabled;
    },
    [rsimEnability],
  );

  const isSecondaryProfileDisabled = useCallback(
    (euicc: keyof RSimEnabilities) => {
      if (!rsimEnability) {
        return false;
      }

      return rsimEnability?.[euicc]?.isSecondaryProfileDisabled;
    },
    [rsimEnability],
  );

  const queryClient = useQueryClient();
  const {
    error,
    reset,
    mutate: kickSim,
  } = useMutation({
    mutationFn: ({ enabled, euicc, profileId }: KickSimMutationArgs) =>
      setRSimEnability(euicc, profileId, enabled),
    onMutate: async ({ enabled, euicc, profileId }) => {
      const key = queryKey ? [queryKey] : rSimProductsQueries.enability({ euiccs });
      await queryClient.cancelQueries({ queryKey });

      const oldData = queryClient.getQueryData<RSimEnabilities>(key);

      queryClient.setQueryData<RSimEnabilities>(key, (data) => {
        if (!data) {
          throw new Error('No data to update');
        }

        return {
          ...data,
          [euicc]: {
            isPrimaryProfileDisabled:
              profileId === 0 ? !enabled : data[euicc].isPrimaryProfileDisabled,
            isSecondaryProfileDisabled:
              profileId === 1 ? !enabled : data[euicc].isSecondaryProfileDisabled,
          },
        };
      });

      return { queryKey: key, oldData };
    },
    onError: (_err, _variables, context) => {
      if (context?.oldData) {
        queryClient.setQueryData(context.queryKey, context.oldData);
      }
    },
  });

  return (
    <KickSimContext.Provider
      value={{
        isPrimaryProfileDisabled,
        isSecondaryProfileDisabled,
        kickSim,
      }}
    >
      {children}
      <AutoHideSnackbar severity="error" open={!!error} onClose={() => reset()}>
        <AlertTitle>{t('common.requestFailed')}!</AlertTitle>
        <Typography variant="text1" color="text.white">
          {t('common.somethingWrong')}
        </Typography>
      </AutoHideSnackbar>
    </KickSimContext.Provider>
  );
};
